import {isProdEnv} from 'common/report/util/env';

import type {ScopedLocalizationStore} from './store';
import {ABSOLUTE_NESTING_MARKER, qualifyNestings} from './nesting';
import {registerScopeTranslationLoaders, type ScopeTranslationLoaders} from './scope-translations';
import {getFlattenedDictionary, getTranslationText} from './util';
import {i18next, type TranslationStore} from './index';

const PLURAL_SUFFIXES = [`zero`, `one`, `two`, `few`, `many`, `other`, `plural`] as const;

/** Flat translation key map: each key is a scope-relative dotted path such as `time.less-than-minute-past`. */
export type TranslationKeyMap = {readonly [key: string]: string};

type PluralBase<Key extends string> = Key extends `${infer Base}_${(typeof PLURAL_SUFFIXES)[number]}` ? Base : never;

/** `x_user` yields `x`: i18next strips one trailing `_<context>` from the last key segment. */
type ContextBase<Key extends string> = Key extends `${infer Head}_${infer Tail}`
  ? Tail extends `${string}_${string}`
    ? `${Head}_${ContextBase<Tail>}`
    : Tail extends `${string}.${string}`
      ? never
      : Head
  : never;

/**
 * For `x_one`/`x_other`/... yields the key and the base `x` that i18next resolves with `count`. For a plural
 * context form `x_user_one` that includes the context key `x_user` and the base `x` that takes a `context` option,
 * the way i18next resolves them. A plain context form `x_admin` includes `x`.
 */
type WithPluralBases<Key extends string> = Key | PluralBase<Key> | ContextBase<Key> | ContextBase<PluralBase<Key>>;

/** Keys addressable in a scope, relative to the scope (no scope id). Accepts the translation key map or the `I18nScope` handle. */
export type ScopeTranslationKey<Source> =
  Source extends I18nScope<infer Keys> ? WithPluralBases<keyof Keys & string> : WithPluralBases<keyof Source & string>;

declare const STORAGE_KEY_BRAND: unique symbol;

/**
 * A full storage key (`<scope id>.<key>`) supplied by a caller from its own dictionary. Translate it with
 * `translateKey`. Only `scope.qualify(key)` produces one, so a hand-built string does not typecheck.
 */
export type StorageKey = string & {readonly [STORAGE_KEY_BRAND]: true};

/** The `t` function of the store scoped to `Source`, which is a scope handle (`typeof translationKeys`) or its translation key map. */
export type TranslateFor<Source> = ScopedLocalizationStore<
  Source extends I18nScope<infer Tree> ? Tree : Source extends TranslationKeyMap ? Source : never
>[`t`];

export interface I18nScope<Keys extends TranslationKeyMap = TranslationKeyMap> {
  /** Folder path relative to `iron/` with `/` replaced by `.`, e.g. `apps.settings.src.audit-log-table`. */
  readonly id: string;
  /** The raw translation keys as written in the scope file (`withComment`/`withDetail` leaves are objects at runtime). */
  readonly translationKeys: Keys;
  /** Full storage key (`<id>.<key>`) of a scope-relative key. */
  qualify(key: ScopeTranslationKey<Keys>): StorageKey;
  /** Whether the key (or a plural or context form of it) is defined in this scope. For keys only known at runtime. */
  has(key: string): key is ScopeTranslationKey<Keys>;
  /**
   * Registers where this scope's foreign-locale translations load from. The build adds the call to every scope
   * module (`iron/build/vite/plugins/i18n-scopes.mts`); a scope without it has English only.
   */
  registerTranslationLoaders(loaders: ScopeTranslationLoaders): void;
}

const SCOPE_ID_PATTERN = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/;

type RegisteredScope = {scope: I18nScope<any>; flatKeys: ReadonlySet<string>};

const registry = new Map<string, RegisteredScope>();
/** Full storage key -> English text by owning scope id, for detecting keys claimed by two different scopes. */
const keyOwners = new Map<string, Map<string, string>>();

/** Qualified English text of every entry in a scope, keyed by its scope-relative key. */
export function getScopeEnglishEntries(scopeId: string, translationKeys: TranslationKeyMap): Record<string, string> {
  const entries: Record<string, string> = {};
  for (const [key, entry] of Object.entries(getFlattenedDictionary(translationKeys, true))) {
    entries[key] = qualifyNestings(getTranslationText(entry), scopeId);
  }
  return entries;
}

function assertNoKeyCollisions(scopeId: string, qualifiedEntries: Record<string, string>) {
  for (const [key, text] of Object.entries(qualifiedEntries)) {
    const fullKey = `${scopeId}.${key}`;
    const owners = keyOwners.get(fullKey);
    for (const [ownerScopeId, ownerText] of owners ?? []) {
      if (ownerScopeId !== scopeId && ownerText !== text) {
        throw new Error(
          `i18n key collision on "${fullKey}": scope "${ownerScopeId}" and scope "${scopeId}" define it differently`,
        );
      }
    }
  }
}

/**
 * Declares the translations owned by one folder. Call it from the folder's `index.i18n.ts` as the default export;
 * the English text is registered with i18next as `<id>.<key>` when the module is evaluated.
 */
export function createI18nScope<const Keys extends TranslationKeyMap>(
  id: string,
  translationKeys: Keys,
): I18nScope<Keys> {
  if (!SCOPE_ID_PATTERN.test(id)) {
    throw new Error(`Invalid i18n scope id "${id}": use dot-separated segments of letters, digits, "_" and "-"`);
  }

  const qualifiedEntries = getScopeEnglishEntries(id, translationKeys);
  const flatKeys = new Set(Object.keys(qualifiedEntries));
  // Keys that i18next resolves from the `count` and `context` options: the key without its plural suffix and/or
  // without one `_<context>` suffix on the last segment.
  const optionResolvedBases = new Set<string>();
  const stripContext = (key: string): string | undefined => {
    const underscoreIndex = key.lastIndexOf(`_`);
    return underscoreIndex > key.lastIndexOf(`.`) ? key.slice(0, underscoreIndex) : undefined;
  };
  for (const flatKey of flatKeys) {
    const pluralSuffix = PLURAL_SUFFIXES.find((suffix) => flatKey.endsWith(`_${suffix}`));
    const pluralBase = pluralSuffix ? flatKey.slice(0, -(pluralSuffix.length + 1)) : undefined;
    for (const candidate of [pluralBase, stripContext(flatKey), pluralBase && stripContext(pluralBase)]) {
      if (candidate) {
        optionResolvedBases.add(candidate);
      }
    }
  }

  if (!isProdEnv()) {
    assertNoKeyCollisions(id, qualifiedEntries);
  }

  for (const previousKey of registry.get(id)?.flatKeys ?? []) {
    if (!flatKeys.has(previousKey)) {
      const fullKey = `${id}.${previousKey}`;
      const owners = keyOwners.get(fullKey);
      owners?.delete(id);
      if (!owners?.size) {
        keyOwners.delete(fullKey);
      }
    }
  }

  const scope: I18nScope<Keys> = {
    id,
    translationKeys,
    qualify: (key) => `${id}.${key}` as StorageKey,
    has: (key): key is ScopeTranslationKey<Keys> => flatKeys.has(key) || optionResolvedBases.has(key),
    registerTranslationLoaders: (loaders) => registerScopeTranslationLoaders(id, loaders),
  };

  // Re-evaluation (HMR) replaces the previous registration of the same id.
  registry.set(id, {scope, flatKeys});
  for (const [key, text] of Object.entries(qualifiedEntries)) {
    const fullKey = `${id}.${key}`;
    const owners = keyOwners.get(fullKey) ?? new Map<string, string>();
    owners.set(id, text);
    keyOwners.set(fullKey, owners);
  }

  const flattenedWithScopeId = Object.fromEntries(
    Object.entries(qualifiedEntries).map(([key, text]) => [`${id}.${key}`, text]),
  );
  i18next.addResourceBundle(`en`, `translation`, flattenedWithScopeId, true, true);

  return scope;
}

/**
 * Nesting that points at a key owned by another scope: `ref(otherScope, 'some.key')` as a translation value renders
 * as `$t(<otherScope.id>.some.key)` and is not re-qualified into the referencing scope.
 */
export function ref<Keys extends TranslationKeyMap>(other: I18nScope<Keys>, key: ScopeTranslationKey<Keys>): string {
  return `${ABSOLUTE_NESTING_MARKER}$t(${other.qualify(key)})`;
}

/**
 * Builds a translatable (a function that `print` calls with the store) whose `render` receives a store scoped to
 * `scope`. The return type is inferred, so branded results such as `LocaleSafeString` keep flowing through
 * `StrictTranslatable` positions.
 */
export function translatable<Keys extends TranslationKeyMap, Result extends string = string>(
  scope: I18nScope<Keys>,
  render: (i18n: ScopedLocalizationStore<Keys>) => Result,
): (i18n: TranslationStore) => Result {
  return (i18n) => render(i18n.scoped(scope));
}

/** @public */
export function getRegisteredScopes(): ReadonlyMap<string, RegisteredScope> {
  return registry;
}
